if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
      
      // Initialiser les flèches de navigation
      this.initArrows();
    }

    initArrows() {
      // Créer les flèches
      const prevArrow = document.createElement('button');
      const nextArrow = document.createElement('button');
      
      prevArrow.className = 'gallery-arrow gallery-arrow--prev';
      nextArrow.className = 'gallery-arrow gallery-arrow--next';
      
      prevArrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
      nextArrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      
      prevArrow.setAttribute('aria-label', 'Image précédente');
      nextArrow.setAttribute('aria-label', 'Image suivante');
      prevArrow.setAttribute('type', 'button');
      nextArrow.setAttribute('type', 'button');
      
      // Ajouter les flèches au viewer
      this.elements.viewer.appendChild(prevArrow);
      this.elements.viewer.appendChild(nextArrow);
      
      // Ajouter les événements
      prevArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateToPrevious();
      });
      
      nextArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateToNext();
      });
      
      // Stocker les références
      this.elements.prevArrow = prevArrow;
      this.elements.nextArrow = nextArrow;
      
      // Mettre à jour l'état des flèches
      this.updateArrowsState();
    }

    getCurrentIndex() {
      // Utiliser les thumbnails pour déterminer l'index actuel
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const activeThumbnail = this.elements.thumbnails.querySelector('[data-target] button[aria-current="true"]')?.parentElement;
      
      if (activeThumbnail) {
        return thumbnails.indexOf(activeThumbnail);
      }
      
      // Sinon, chercher dans le viewer
      const allMedia = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
      const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
      
      if (activeMedia) {
        return allMedia.indexOf(activeMedia);
      }
      
      return 0; // Par défaut, première image
    }

    navigateToPrevious() {
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const currentIndex = this.getCurrentIndex();
      
      if (currentIndex > 0 && thumbnails[currentIndex - 1]) {
        const targetMediaId = thumbnails[currentIndex - 1].dataset.target;
        // Simuler le clic sur le thumbnail
        const button = thumbnails[currentIndex - 1].querySelector('button');
        if (button) {
          button.click();
        }
      }
    }

    navigateToNext() {
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const currentIndex = this.getCurrentIndex();
      
      if (currentIndex < thumbnails.length - 1 && thumbnails[currentIndex + 1]) {
        const targetMediaId = thumbnails[currentIndex + 1].dataset.target;
        // Simuler le clic sur le thumbnail
        const button = thumbnails[currentIndex + 1].querySelector('button');
        if (button) {
          button.click();
        }
      }
    }

    updateArrowsState() {
      if (!this.elements.prevArrow || !this.elements.nextArrow) return;
      
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const currentIndex = this.getCurrentIndex();
      
      // Désactiver/activer les flèches selon la position
      this.elements.prevArrow.disabled = currentIndex <= 0;
      this.elements.nextArrow.disabled = currentIndex >= thumbnails.length - 1;
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.updateArrowsState();
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      if(this.dataset.scrollback === "true"){
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({behavior: 'smooth'});
          }
        });
      }
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      
      // Mettre à jour l'état des flèches
      this.updateArrowsState();
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}